import React, { useState, useEffect, useRef, useCallback } from "react";
import { addJob, deleteJob } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function AdministrativeJobAdminPanel({ darkMode }) {
  const [formData, setFormData] = useState({ title: "", slug: "" });
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [jobs, setJobs] = useState([]);
  const [displayJobs, setDisplayJobs] = useState([]);
  const [jobsOffset, setJobsOffset] = useState(0);
  const [jobsHasMore, setJobsHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const listRef = useRef(null);
  const PAGE_LIMIT = 25;
  const role = localStorage.getItem("role");

  // Initial load: only fetch jobs section
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      if (!threeTable?.jobs?.length && !ignore) {
        await fetchThreeTableSection("jobs", false, { limit: PAGE_LIMIT, offset: 0 });
      }
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, []);

  // Sync jobs from context
  useEffect(() => {
    setJobs(threeTable.jobs || []);
    setDisplayJobs([]);
    setJobsOffset(0);
    setJobsHasMore(true);
  }, [threeTable.jobs]);

  // Infinite scroll fetch for jobs
  const fetchMoreJobs = useCallback(async () => {
    if (!jobsHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    const offset = jobsOffset;
    await fetchThreeTableSection("jobs", false, { limit: PAGE_LIMIT, offset });
    setIsFetchingMore(false);
  }, [jobsHasMore, isFetchingMore, jobsOffset, fetchThreeTableSection]);

  // Initial display batch
  useEffect(() => {
    if (jobs.length > 0 && displayJobs.length === 0) {
      setDisplayJobs(jobs.slice(0, PAGE_LIMIT));
      setJobsOffset(Math.min(PAGE_LIMIT, jobs.length));
      setJobsHasMore(PAGE_LIMIT < jobs.length);
    } else if (jobs.length > displayJobs.length) {
      setDisplayJobs(jobs.slice(0, jobs.length));
      setJobsOffset(jobs.length);
      setJobsHasMore(jobs.length % PAGE_LIMIT === 0);
    }
  }, [jobs, displayJobs.length]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!jobsHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreJobs();
    }
  }, [jobsHasMore, isFetchingMore, fetchMoreJobs]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Job submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.slug.trim()) {
      alert("Please enter both job title and job slug.");
      return;
    }
    await addJob({ ...formData, showOnHome: false });
    alert("Job added! (Hidden by default)");
    setFormData({ title: "", slug: "" });
    await fetchThreeTableSection("jobs", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this job?")) {
      await deleteJob(id);
      alert("Job deleted!");
      await fetchThreeTableSection("jobs", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  if (role !== "administrative") {
    return <div className="admin-panel-error">❌ Access denied! Only Administrative users can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--emerald${darkMode ? ' dark-mode' : ''}`}> 
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">🛡️ Administrative Job Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Yahan se administrative jobs manage karo. Job add karo, list me dekho, home par visibility toggle karo ya delete karo.</p>
        </div>
        <div className="resource-admin-panel__pill">Administrative Jobs</div>
      </div>
      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label className="resource-admin-panel__label">Job Title</label>
          <input
            className="resource-admin-panel__input"
            placeholder="Job Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div className="resource-admin-panel__field">
          <label className="resource-admin-panel__label">Job Slug</label>
          <input
            className="resource-admin-panel__input"
            placeholder="Job Slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
        </div>
        <button className="resource-admin-panel__submit" type="submit">✅ Add Job</button>
      </form>
      <div className="resource-admin-panel__section">
        <div className="resource-admin-panel__section-top">
          <div>
            <h3 className="resource-admin-panel__section-title">Saved Administrative Jobs</h3>
            <p className="resource-admin-panel__section-meta">{jobs.length} items</p>
          </div>
        </div>
        {jobs.length === 0 ? (
          <p className="resource-admin-panel__empty">No jobs found</p>
        ) : (
          <div ref={listRef} className="resource-admin-panel__list">
            {displayJobs.map((job) => (
              <div key={job._id} className="resource-admin-panel__item">
                <div>
                  <p className="resource-admin-panel__item-title">{job.title}</p>
                  <p className="resource-admin-panel__item-subtitle">Slug: {job.slug}</p>
                  <span style={{ marginLeft: "8px", color: job.showOnHome === false ? "#d32f2f" : "#2e7d32", fontWeight: "bold" }}>
                    {job.showOnHome === false ? "Hidden on Home" : "Visible on Home"}
                  </span>
                </div>
                <div className="resource-admin-panel__actions">
                  <a
                    href={`/mform/${job.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-admin-panel__link"
                  >
                    View Live
                  </a>
                    {/* Show/Hide on Home button is intentionally hidden in this panel */}
                    {job.showOnHome === false && (
                      <button
                        className="resource-admin-panel__delete"
                        onClick={() => handleDelete(job._id)}
                      >
                        Delete
                      </button>
                    )}
                </div>
              </div>
            ))}
            {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
            {!jobsHasMore && !isFetchingMore && displayJobs.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more jobs.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdministrativeJobAdminPanel;
